import { ReactElement, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import Decimal from 'decimal.js'
import { find } from 'lodash-es'
import { v4 as uuid } from 'uuid'
import { Stack, Typography } from '@mui/material'
import CreateElementLabelField from '@editor/pages/StructuralPlanning/components/CreateElementLabelField'
import {
  applyCreateLabel,
  createLabelSchema,
} from '@editor/pages/StructuralPlanning/components/RectangularShape/components/RectangularShapeForm/components/GeometryForm/schema'
import RipLineFormSceneMarker from '@sceneMarkers/FormSceneMarkers/RipLineFormSceneMarker'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { HotkeySaveButton, SaveButton } from '@ui/actions'
import { EditableLabel, ErrorField, Form } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { useElementLabel, useResultsInvalidation } from '@editorHooks'
import { useStructuralPlanningSelection } from '@structuralPlanningHooks'
import { getModel, getVerticalTransmissionGraph } from '@queries'
import { createRips, deleteRip } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import { createDefaultRipForWall } from '../..'
import { lineLikeSettingsOf } from '../../../PlanSettingsForm/utils'
import FormFields from '../FormFields'
import { createRipSchema } from './schema'

const ABS_POSITION_NUM_DECIMALS = 2

interface Props {
  positionGuid?: string
  wallGuid?: string
  isNewRip?: boolean
}

export const WallRipFormBase = ({
  positionGuid,
  wallGuid,
  isNewRip = false,
}: Props): ReactElement | null => {
  const [newRipGuid] = useState(() => uuid())
  const resolvedPositionGuid = isNewRip ? newRipGuid : positionGuid

  const { projectId } = useParams()

  const { enqueueSnackbar } = useSnackbar()
  const client = useQueryClient()
  const invalidateResults = useResultsInvalidation()

  const getLabel = useElementLabel()
  const label = resolvedPositionGuid ? getLabel(resolvedPositionGuid) : ''

  const {
    actions: { resetElements },
  } = useStructuralPlanningSelection()

  const removeRip = useModelStore(state => state.removeRip)

  const rips = useModelStore(state => state.model.rips)

  const rip = useMemo(
    () =>
      isNewRip || !resolvedPositionGuid
        ? undefined
        : find(rips, { position_guid: resolvedPositionGuid }),
    [isNewRip, rips, resolvedPositionGuid],
  )

  const { walls } = useModelStore(state => state.model)
  const wall = useMemo(() => {
    const resolvedWallGuid = rip?.wall_guid ?? wallGuid
    if (resolvedWallGuid) {
      return find(walls, { guid: resolvedWallGuid })
    }
  }, [rip, wallGuid, walls]) as ShapeObject | undefined

  const wallLength = useMemo(() => wall?.shape.points[0].distanceTo(wall?.shape.points[1]), [wall])

  const { schema, defaultValues } = useMemo(() => {
    if (!wall || !wallLength) {
      return {
        schema: createRipSchema(resolvedPositionGuid ?? '', wallGuid ?? ''),
        defaultValues: {},
      }
    }

    const defaultRip = isNewRip ? createDefaultRipForWall(wall) : null
    const ripForDefaults = rip ?? defaultRip

    const absolutePosition = ripForDefaults
      ? wall.shape.points[0].distanceTo(ripForDefaults.start)
      : 0
    const absolutePositionRounded = absolutePosition.toFixed(ABS_POSITION_NUM_DECIMALS)
    const wallLengthRounded = wallLength.toFixed(ABS_POSITION_NUM_DECIMALS)
    const relativePosition = new Decimal(absolutePositionRounded).div(
      new Decimal(wallLengthRounded),
    )
    const relativePositionAsString =
      ripForDefaults?.relative_position ?? relativePosition.toString()

    const geometrySchema = createRipSchema(resolvedPositionGuid ?? '', wall.guid)
    const showCreateLabel = isNewRip || !!rip?.is_local
    const schema = showCreateLabel ? geometrySchema.concat(createLabelSchema) : geometrySchema

    const defaultValues = {
      ...geometrySchema.getDefault(),
      relative_position: relativePositionAsString,
      ...(showCreateLabel ? { label: '' } : {}),
      ...(ripForDefaults && {
        rip_start: {
          x: ripForDefaults.start.x,
          y: ripForDefaults.start.y,
          z: ripForDefaults.start.z,
        },
        rip_end: { x: ripForDefaults.end.x, y: ripForDefaults.end.y, z: ripForDefaults.end.z },
      }),
    }

    return { schema, defaultValues }
  }, [isNewRip, rip, wall, wallLength, resolvedPositionGuid, wallGuid])

  const { mutateAsync, isLoading } = useMutation(
    async (ripData: WallRipRequest) => {
      if (isNewRip || rip?.is_local) {
        return await createRips.request(projectId, [ripData])
      }
      await deleteRip.request(projectId, ripData.guid)
      return await createRips.request(projectId, [ripData])
    },
    {
      onSuccess: async () => {
        if (rip?.is_local) {
          removeRip(rip.position_guid)
        }
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Wandrippe erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Wandrippe'), {
          variant: 'error',
        })
      },
    },
  )

  const resetSelection = () => {
    resetElements()
  }

  const showCreateLabel = isNewRip || !!rip?.is_local

  const onSubmit = async (formData: WallRipRequest) => {
    // Re-creating an existing rip keeps its plan settings.
    const data = { ...formData, ...lineLikeSettingsOf(rip) }
    await mutateAsync(showCreateLabel ? applyCreateLabel(data, data.label) : data)
    resetSelection()
  }

  if (!wall || !wallLength || !resolvedPositionGuid) {
    return null
  }

  const SubmitButton = showCreateLabel ? HotkeySaveButton : SaveButton

  return (
    <>
      <Form
        key={`positionGuid-${isNewRip ? 'new' : rip?.relative_position}`}
        onSubmit={onSubmit}
        validationSchema={schema}
        defaultValues={defaultValues}
        validationContext={{ wall, rips }}
      >
        <Stack direction="column" spacing={2}>
          <RipLineFormSceneMarker />
          {showCreateLabel ? (
            <>
              {isNewRip && <Typography>Neue Rippe</Typography>}
              <CreateElementLabelField />
            </>
          ) : (
            <EditableLabel elementGuid={resolvedPositionGuid} label={label} />
          )}
          <FormFields guid={resolvedPositionGuid} wallGuid={wall.guid} length={wallLength} />

          <ErrorField name="isIntersecting" />
          <ErrorField name="isOverlapping" />

          <SubmitButton type="submit" fullWidth loading={isLoading} data-cy="btn-save-rip">
            Speichern
          </SubmitButton>
        </Stack>
      </Form>
    </>
  )
}
